// utils/LeagueContext.js
import { createContext, useContext, useState, useEffect } from "react";
import { useUser } from "../UserContext";
import API_BASE_URL from './apiConfig'; // Import API base URL

const LeagueContext = createContext();

export const LeagueProvider = ({ children }) => {
  const { user } = useUser();
  const [leagueId, setLeagueId] = useState(localStorage.getItem("leagueId") || "");
  const [selectedLeague, setSelectedLeague] = useState(null);
  const currentYear = new Date().getFullYear();
  const [selectedSeason, setSelectedSeason] = useState(
    parseInt(localStorage.getItem("selectedSeason")) || currentYear
  );
  const getToken = () => {
    return localStorage.getItem("token") || user?.token || null;
  };

  const fetchLeagueById = async (leagueId) => {
    const token = getToken();

    if (!token) throw new Error("No token found for league fetch");

    const response = await fetch(`${API_BASE_URL}/api/leagues/${leagueId}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
      credentials: "include", // Include cookies
    });

    if (!response.ok) throw new Error("Failed to fetch league");
    return await response.json();
  };

  const setSelectedLeagueAndId = async (
    newLeagueId,
    allowFallback = true,
    preloadedLeague = null
  ) => {
    if (!newLeagueId) return;

    const token = getToken();
    if (!token) {
      console.warn("Delaying league fetch until token is available");
      return;
    }

    try {
      const league = await fetchLeagueById(newLeagueId);
      setLeagueId(newLeagueId);

      setSelectedLeague({
        ...league,
        inviteStatus: preloadedLeague?.inviteStatus || league?.inviteStatus,
        role: preloadedLeague?.role || league?.role,
      });
    } catch (err) {
      console.error("Failed to fetch and enrich league:", err);

      if (allowFallback && preloadedLeague) {
        console.warn("⚠️ Falling back to preloadedLeague (not fully enriched):", preloadedLeague);
        setLeagueId(newLeagueId);
        setSelectedLeague(preloadedLeague);
      } else {
        setSelectedLeague(null);
      }
    }
  };

  // Persist leagueId to localStorage when it changes
  useEffect(() => {
    if (leagueId) {
      localStorage.setItem("leagueId", leagueId);
    }
  }, [leagueId]);

  useEffect(() => {
    localStorage.setItem("selectedSeason", selectedSeason);
  }, [selectedSeason]);

  return (
    <LeagueContext.Provider value={{
      leagueId,
      selectedLeague,
      setLeagueId,
      setSelectedLeague,
      setSelectedLeagueAndId,
      selectedSeason,
      setSelectedSeason,
    }}>
      {children}
    </LeagueContext.Provider>
  );
};

export const useLeague = () => useContext(LeagueContext);
